import { FC, ReactNode } from 'react';
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { Edit, Lock } from '@mui/icons-material';
import { useLockedOptionsSectionController } from './useLockedOptionsSectionController';

interface Props {
  title: string;
  // Renders the options, disabled while the section is locked.
  children: (locked: boolean) => ReactNode;
}

/**
 * Options that change what a team's participants are asked, such as survey and questionnaire assignment. They're
 * locked so a single misclick can't change them: an admin clicks the pencil to unlock them, changes them, then saves
 * the team.
 */
export const LockedOptionsSection: FC<Props> = ({ title, children }) => {
  const section = useLockedOptionsSectionController();
  return (
    <>
      <Divider sx={{ mt: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{title}</Typography>
          {section.editing ? (
            <Button size="small" onClick={section.handleDoneEditing}>
              Done
            </Button>
          ) : (
            <Tooltip title={`Edit ${title.toLowerCase()}`}>
              <IconButton size="small" onClick={section.handleEdit} aria-label={`Edit ${title.toLowerCase()}`}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          {!section.editing && <Lock sx={{ fontSize: 16 }} />}
          <Typography variant="body2">
            {section.editing
              ? 'Editing. Changes take effect when you save the team.'
              : 'Locked to prevent accidental changes. Click the pencil to edit.'}
          </Typography>
        </Box>
        {children(!section.editing)}
      </Box>
    </>
  );
};
